import type { Stage } from '@my-roadtrip/types'
import { calculateDistance } from '@my-roadtrip/utils'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('RouteOptimizer')

export class RouteOptimizer {
  /**
   * Optimize stage order using nearest neighbor algorithm
   * Preserves first and last stages if they exist
   */
  optimizeRoute(stages: Stage[]): Stage[] {
    logger.debug('Optimizing route', { stagesCount: stages.length })

    if (stages.length <= 2) {
      logger.debug('Route too short to optimize', { stagesCount: stages.length })
      return stages
    }

    const sorted = [...stages].sort((a, b) => a.order - b.order)
    logger.debug('Stages sorted by order', { first: sorted[0]?.name, last: sorted.at(-1)?.name })
    
    // Keep first stage fixed
    const start = sorted[0]
    const middle = sorted.slice(1, -1)
    const end = sorted.at(-1)!

    logger.debug('Route optimization parameters', { 
      startStage: start.name,
      middleStagesCount: middle.length,
      endStage: end.name
    })

    // Optimize middle stages
    const optimized = this.nearestNeighbor(middle, start)
    logger.info('Middle stages optimized using nearest neighbor', { 
      originalOrder: middle.map(s => s.name).join(' → '),
      optimizedOrder: optimized.map(s => s.name).join(' → ')
    })

    // Reassign orders
    const result = [start, ...optimized, end]
    const withNewOrder = result.map((stage, index) => ({
      ...stage,
      order: index
    }))

    const newTotalDistance = this.calculateTotalDistance(withNewOrder)
    const oldTotalDistance = this.calculateTotalDistance(stages)
    const improvement = ((oldTotalDistance - newTotalDistance) / oldTotalDistance * 100).toFixed(2)

    logger.info('Route optimization complete', { 
      originalDistance: `${oldTotalDistance.toFixed(2)}km`,
      optimizedDistance: `${newTotalDistance.toFixed(2)}km`,
      improvement: `${improvement}%`
    })

    return withNewOrder
  }

  /**
   * Nearest neighbor algorithm for TSP-like optimization
   */
  private nearestNeighbor(stages: Stage[], start: Stage): Stage[] {
    logger.debug('Starting nearest neighbor algorithm', { 
      stagesCount: stages.length,
      startStage: start.name
    })

    if (stages.length === 0) {
      logger.debug('No stages to optimize')
      return []
    }

    const unvisited = [...stages]
    const route: Stage[] = []
    let current = start
    let iterations = 0

    while (unvisited.length > 0) {
      iterations++
      let nearest: Stage | null = null
      let minDistance = Infinity

      for (const stage of unvisited) {
        const distance = calculateDistance(
          current.location.coordinates,
          stage.location.coordinates
        )

        if (distance < minDistance) {
          minDistance = distance
          nearest = stage
        }
      }

      if (!nearest) {
        logger.warn('No nearest stage found', { iteration: iterations, unvisitedCount: unvisited.length })
        break
      }

      logger.debug('Found nearest stage', { 
        from: current.name,
        to: nearest.name,
        distance: `${minDistance.toFixed(2)}km`,
        iteration: iterations
      })

      route.push(nearest)
      current = nearest
      unvisited.splice(unvisited.indexOf(nearest), 1)
    }

    logger.debug('Nearest neighbor algorithm complete', { 
      stagesCount: stages.length,
      routeLength: route.length,
      iterations
    })

    return route
  }

  /**
   * Calculate total distance for a route
   */
  calculateTotalDistance(stages: Stage[]): number {
    logger.debug('Calculating total distance', { stagesCount: stages.length })
    let total = 0
    const distances: number[] = []

    for (let i = 0; i < stages.length - 1; i++) {
      const current = stages[i]
      const next = stages[i + 1]
      
      const distance = calculateDistance(
        current.location.coordinates,
        next.location.coordinates
      )
      distances.push(distance)
      total += distance

      logger.debug('Distance calculated', { 
        from: current.name,
        to: next.name,
        distance: `${distance.toFixed(2)}km`
      })
    }

    logger.info('Total distance calculated', { 
      totalDistance: `${total.toFixed(2)}km`,
      stagesCount: stages.length,
      segments: distances.length,
      averageSegmentDistance: `${(total / distances.length).toFixed(2)}km`
    })

    return total
  }
}
